/**
 * @fileoverview Notification Store
 * @description Zustand store for managing notification state with polling
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import notificationService from '../services/notificationService.js';

export const useNotificationStore = create(
  subscribeWithSelector((set, get) => ({
    // State
    notifications: [],
    unreadCount: 0,
    isLoading: false,
    error: null,
    isPolling: false,
    lastFetchTime: null,
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      totalPages: 0
    },

    // Actions
    setLoading: (loading) => set({ isLoading: loading }),

    setError: (error) => set({ error }),

    /**
     * Fetch notifications from API
     */
    fetchNotifications: async (options = {}) => {
      const { setLoading, setError } = get();
      setLoading(true);
      setError(null);

      try {
        const data = await notificationService.fetchNotifications(options);
        
        set({
          notifications: data.notifications || [],
          pagination: data.pagination || {
            page: 1,
            limit: 20,
            total: 0,
            totalPages: 0
          },
          lastFetchTime: new Date(),
          isLoading: false
        });

        // Also fetch unread count
        await get().fetchUnreadCount();

        return data;
      } catch (error) {
        setError(error.message || 'Failed to fetch notifications');
        setLoading(false);
        throw error;
      }
    },

    /**
     * Fetch unread count
     */
    fetchUnreadCount: async () => {
      try {
        const count = await notificationService.getUnreadCount();
        set({ unreadCount: count });
        return count;
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
        return 0;
      }
    },

    /**
     * Mark notification as read
     */
    markAsRead: async (notificationId) => {
      try {
        await notificationService.markAsRead(notificationId);
        
        // Update local state
        set((state) => ({
          notifications: state.notifications.map(n =>
            n.id === notificationId
              ? { ...n, read: true, readAt: new Date().toISOString() }
              : n
          ),
          unreadCount: Math.max(0, state.unreadCount - 1)
        }));
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
        throw error;
      }
    },

    /**
     * Mark all notifications as read
     */
    markAllAsRead: async () => {
      try {
        const count = await notificationService.markAllAsRead();
        
        // Update local state
        set((state) => ({
          notifications: state.notifications.map(n => ({
            ...n,
            read: true,
            readAt: new Date().toISOString()
          })),
          unreadCount: 0
        }));

        return count;
      } catch (error) {
        console.error('Failed to mark all as read:', error);
        throw error;
      }
    },

    /**
     * Delete a notification
     */
    deleteNotification: async (notificationId) => {
      try {
        await notificationService.deleteNotification(notificationId);
        
        // Update local state
        set((state) => {
          const notification = state.notifications.find(n => n.id === notificationId);
          const wasUnread = notification && !notification.read;
          
          return {
            notifications: state.notifications.filter(n => n.id !== notificationId),
            unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
            pagination: {
              ...state.pagination,
              total: Math.max(0, state.pagination.total - 1)
            }
          };
        });
      } catch (error) {
        console.error('Failed to delete notification:', error);
        throw error;
      }
    },

    /**
     * Start polling for notifications
     */
    startPolling: () => {
      const { isPolling, fetchNotifications, fetchUnreadCount } = get();
      
      if (isPolling) {
        return; // Already polling
      }

      set({ isPolling: true });

      // Callback for polling updates
      const handlePollUpdate = async (data) => {
        const currentState = get();
        
        // Only update if we have new data
        if (data && data.notifications) {
          // Merge with existing notifications, avoiding duplicates
          const existingIds = new Set(currentState.notifications.map(n => n.id));
          const newNotifications = data.notifications.filter(n => !existingIds.has(n.id));
          
          if (newNotifications.length > 0 || data.pagination.total !== currentState.pagination.total) {
            // Refetch to get complete list
            await fetchNotifications();
            await fetchUnreadCount();
          }
        }
      };

      notificationService.startPolling(handlePollUpdate);
    },

    /**
     * Stop polling for notifications
     */
    stopPolling: () => {
      notificationService.stopPolling();
      set({ isPolling: false });
    },

    /**
     * Initialize notifications (fetch and start polling)
     */
    initialize: async () => {
      const { fetchNotifications, fetchUnreadCount, startPolling } = get();
      
      try {
        await fetchNotifications();
        await fetchUnreadCount();
        startPolling();
      } catch (error) {
        console.error('Failed to initialize notifications:', error);
      }
    },

    /**
     * Cleanup (stop polling)
     */
    cleanup: () => {
      get().stopPolling();
    }
  }))
);
